import { ArrowLeftIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import styled, { ThemeProvider } from 'styled-components';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { DocumentFile, DocumentGovernmentId } from 'dashboard/models/document';
import DocumentViewerDownload from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerDownload';
import DocumentViewerHeader from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerHeader';
import DocumentViewerRenderer from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerRenderer';
import DocumentViewerRotate from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerRotate';
import DocumentViewerZoom from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerZoom';
import GovernmentIdSidebar from 'dashboard/views/DashboardDocumentViewerView/GovernmentIdSidebar';
import Button from 'design-system/components/Button';
import { paragraphMixins } from 'design-system/mixins/text';
import { darkTheme } from 'design-system/theme';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

interface DocumentGovernmentIdViewerProps {
  document: DocumentGovernmentId;
  featureFlags: FeatureFlagsMap;
  fileLoading: boolean;
  rotation: number;
  rotateDocument: () => void;
  setZoomScale: (zoomScale: number) => void;
  zoomScale: number;
  showMessage: (message: string, messageType: MessageType) => void;
  nullStateMessage: string;
  nullStateRedirect: string;
  incrementZoomScale: () => void;
  decrementZoomScale: () => void;
  zoomVisualValue: string;
  setZoomVisualValue: (zoomVisualValue: string) => void;
}

const DocumentViewerWrapper = styled('div')`
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.background.primary};
  color-scheme: dark;
`;

const HeaderLeftItems = styled('div')`
  display: flex;
  align-items: center;
`;

const HeaderCenterItems = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DocumentContent = styled('div')`
  display: flex;
  min-height: 0;
  height: 100%;
  gap: 40px;
  padding: 0 40px 0;
  @media only screen and (max-width: 900px) {
    overflow-x: auto;
  }
`;

const FilenameLabel = styled('span')`
  ${paragraphMixins.lgBold}
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const DocumentGovernmentIdViewer = ({
  document,
  showMessage,
  fileLoading,
  rotateDocument,
  rotation,
  zoomScale,
  nullStateMessage,
  nullStateRedirect,
  setZoomScale,
  incrementZoomScale,
  decrementZoomScale,
  zoomVisualValue,
  setZoomVisualValue,
}: DocumentGovernmentIdViewerProps) => {
  const files = [
    document.attributes['front-photos-processed']?.[0],
    document.attributes['back-photos-processed']?.[0],
  ].filter((x): x is DocumentFile => x != null);
  const documentFileContainerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case '=':
            e.stopPropagation();
            e.preventDefault();
            incrementZoomScale();
            break;
          case '-':
            e.stopPropagation();
            e.preventDefault();
            decrementZoomScale();
            break;
          case '0':
            e.stopPropagation();
            e.preventDefault();
            setZoomScale(1);
            break;
        }
      }
    },
    [incrementZoomScale, decrementZoomScale, setZoomScale],
  );

  window.addEventListener('keydown', handleKeyDown);

  const inquiryId = document?.relationships.inquiry?.data?.id;

  if (files == null && fileLoading) {
    return <LoadingIndicator fullscreen />;
  } else if (files.length === 0) {
    if (document.attributes['redacted-at']) {
      return (
        <NullState
          message='The document you are accessing has been redacted and is no longer available. This may have been done manually, or as a result of a redaction policy set by your administrator.'
          backRoute={nullStateRedirect}
        />
      );
    } else {
      return (
        <NullState
          message='We cannot find the document you are looking for. It is possible that the document is still processing or there was an error during upload. Please try refreshing the page to access the document again.'
          backRoute={nullStateRedirect}
        />
      );
    }
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <DocumentViewerWrapper id='document-viewer' data-test='document-viewer'>
        <DocumentViewerHeader data-test='document-viewer__header'>
          <>
            <HeaderLeftItems>
              <Button
                data-test='document-viewer__header--back-button'
                id='document-viewer__header--back-button'
                onClick={() => {
                  // allow the user to go back if were redirected internally for best UX
                  if (location.state?.internalRedirect) {
                    navigate(-1);
                  } else {
                    navigate(
                      inquiryId ? `/dashboard/inquiries/${inquiryId}` : '/dashboard/inquiries',
                    );
                  }
                }}
                width='auto'
                variant='primary'
                prefixIcon={<ArrowLeftIcon />}
                aria-label='Go back'
              />
              <FilenameLabel>
                {files[0]?.filename}
                {files.length > 1 &&
                  ` and ${files.length - 1} other ${files.length - 1 === 1 ? 'file' : 'files'}`}
              </FilenameLabel>
            </HeaderLeftItems>
            <HeaderCenterItems>
              <DocumentViewerZoom
                setZoomScale={setZoomScale}
                zoomScale={zoomScale}
                incrementZoom={incrementZoomScale}
                decrementZoom={decrementZoomScale}
                zoomVisualValue={zoomVisualValue}
                setZoomVisualValue={setZoomVisualValue}
              />
              <DocumentViewerRotate rotateDocument={rotateDocument} />
            </HeaderCenterItems>
            <DocumentViewerDownload documentToken={document?.id} files={files} />
          </>
        </DocumentViewerHeader>
        <DocumentContent>
          <DocumentViewerRenderer
            files={files}
            rotation={rotation}
            nullStateMessage={nullStateMessage}
            nullStateRedirect={nullStateRedirect}
            showMessage={showMessage}
            zoomScale={zoomScale}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            documentFileContainerRef={documentFileContainerRef}
          />
          <GovernmentIdSidebar document={document} />
        </DocumentContent>
      </DocumentViewerWrapper>
    </ThemeProvider>
  );
};
